/**
 * Material Transfers (MTV) — Sprint 2.2
 * ==============================================================
 * Inter-project stock movement WITHOUT consumption. Posts paired
 * Transfer_Out / Transfer_In cost events with hits_pnl=False, so
 * cross-project transfers move Net Commitment without polluting P&L.
 *
 * Page structure:
 *   • List view with filters (source, destination, date range, status)
 *   • "+ New Transfer" → MaterialTransferModal
 *   • Per-row actions: View, Cancel Approval (if posted), Delete (if draft)
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card, StatusBadge, EmptyState } from "@/components/ui-helpers";
import { Plus, X, Search, ArrowRight, Trash2, Eye, RotateCcw, Download, Upload, FileSpreadsheet } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useFormTab } from "@/hooks/useFormTab";
import { useClientPagination } from "@/hooks/useClientPagination";
import { PaginationControls } from "@/components/PaginationControls";
import { formatUser, useUserDirectory } from "@/components/UserLabel";
import { SearchableSelect } from "@/components/SearchableSelect";

// Jun-2026 — warehouse ↔ ops-project stock legs (owner-approved)
const TYPE_LABELS = {
  Material: "Material",
  Asset: "Asset",
  WarehouseToProject: "WH → Ops Project",
  ProjectToWarehouse: "Ops Project → WH",
};

export default function MaterialTransfersPage() {
  useUserDirectory();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");           // "" | "Material" | "Asset"
  const openFormTab = useFormTab();

  const load = () => {
    setLoading(true);
    api.get("/material-transfers")
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error("Could not load transfers"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterStatus && r.status !== filterStatus) return false;
      if (filterType && (r.transfer_type || "Material") !== filterType) return false;
      if (!q) return true;
      return (r.voucher_number || "").toLowerCase().includes(q)
        || (r.source_project_name || "").toLowerCase().includes(q)
        || (r.destination_project_name || "").toLowerCase().includes(q)
        || (r.transfer_reason || "").toLowerCase().includes(q);
    });
  }, [rows, search, filterStatus, filterType]);

  const { paged, page, pageSize, total, setPage, setPageSize } = useClientPagination(filtered, { initialPageSize: 50 });

  const openNew = () => openFormTab({
    path: "/material-transfers/new", label: "New Material Transfer",
    icon: Plus, parent: "/material-transfers", onSaved: load,
    render: ({ close }) => <MaterialTransferModal onClose={close} onSaved={() => { load(); close(); }} />,
  });
  const openView = (m) => openFormTab({
    path: `/material-transfers/${m.id}`, label: `MTV · ${m.voucher_number}`,
    parent: "/material-transfers",
    render: ({ close }) => <MaterialTransferView mtv={m} onClose={close} onReload={load} />,
  });

  const handleDelete = async (m) => {
    if (!window.confirm(`Delete draft MTV ${m.voucher_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/material-transfers/${m.id}`);
      toast.success("MTV deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const handleCancelApproval = async (m) => {
    if (!window.confirm(`Cancel approval of ${m.voucher_number}? This will restore the source batch quantity and remove the destination batch.`)) return;
    try {
      await api.post(`/material-transfers/${m.id}/cancel-approval`);
      toast.success("MTV approval cancelled — stock restored");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Inventory · Sprint 2.2"
        title="Material / Asset Transfer"
        subtitle="Move stock (Material) or asset ownership (Asset) between projects · Approval-gated"
        actions={
          <button
            onClick={openNew}
            className="bg-[#F97316] text-white text-xs uppercase tracking-wider font-bold px-4 py-2 flex items-center gap-2"
            data-testid="mtv-new-btn"
          >
            <Plus size={14} /> New Transfer
          </button>
        }
      />

      <Card>
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search voucher / project / reason…"
              className="w-full pl-8 pr-3 py-1.5 border border-slate-300 text-xs focus:border-[#F97316] focus:outline-none"
              data-testid="mtv-search-input"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-slate-300 px-3 py-1.5 text-xs font-bold uppercase tracking-wider"
            data-testid="mtv-status-filter"
          >
            <option value="">All Statuses</option>
            <option value="Draft">Draft</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border border-slate-300 px-3 py-1.5 text-xs font-bold uppercase tracking-wider"
            data-testid="mtv-type-filter"
          >
            <option value="">All Types</option>
            <option value="Material">Material</option>
            <option value="Asset">Asset</option>
            <option value="WarehouseToProject">WH → Ops Project</option>
            <option value="ProjectToWarehouse">Ops Project → WH</option>
          </select>
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            {filtered.length} of {rows.length}
          </span>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={ArrowRight}
            title="No transfers yet"
            subtitle="Move stock between projects (Material) or reassign asset ownership (Asset)"
            cta={{ label: "Create First Transfer", onClick: openNew }}
            testid="mtv-empty-state"
          />
        ) : (
          <table className="w-full text-xs" data-testid="mtv-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-600 font-bold">
                <th className="px-4 py-2">MTV No.</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">From → To</th>
                <th className="px-4 py-2">Reason</th>
                <th className="px-4 py-2 text-right">Value</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {paged.map((m) => (
                <tr key={m.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`mtv-row-${m.voucher_number}`}>
                  <td className="px-4 py-2.5 font-mono text-[11px] font-bold">{m.voucher_number}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                      (m.transfer_type || "Material") === "Asset"
                        ? "bg-indigo-100 text-indigo-800"
                        : m.transfer_type === "WarehouseToProject" || m.transfer_type === "ProjectToWarehouse"
                        ? "bg-violet-100 text-violet-800"
                        : "bg-emerald-100 text-emerald-800"
                    }`} data-testid={`mtv-type-badge-${m.voucher_number}`}>
                      {TYPE_LABELS[m.transfer_type] || m.transfer_type || "Material"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">{(m.voucher_date || "").slice(0, 10)}</td>
                  <td className="px-4 py-2.5">
                    <div className="text-slate-900 font-medium truncate max-w-[180px]" title={m.source_project_name}>
                      {m.source_project_name}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
                      <ArrowRight size={9} className="text-[#F97316]" />
                      <span className="truncate max-w-[170px]" title={m.destination_project_name}>
                        {m.destination_project_name}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-[11px] text-slate-600 truncate max-w-[200px]" title={m.transfer_reason}>
                    {m.transfer_reason || "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{formatINR(m.total_value)}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={m.status} /></td>
                  <td className="px-4 py-2.5">
                    <button onClick={() => openView(m)} className="text-[#0B1F3A] hover:underline mr-3 text-[11px] font-bold" data-testid={`mtv-view-${m.voucher_number}`}>
                      <Eye size={11} className="inline mr-1" />View
                    </button>
                    {m.posted && (
                      <button onClick={() => handleCancelApproval(m)} className="text-amber-700 hover:underline mr-3 text-[11px] font-bold" data-testid={`mtv-cancel-${m.voucher_number}`}>
                        <RotateCcw size={11} className="inline mr-1" />Cancel
                      </button>
                    )}
                    {!m.posted && (
                      <button onClick={() => handleDelete(m)} className="text-rose-600 hover:underline text-[11px]" data-testid={`mtv-delete-${m.voucher_number}`}>
                        <Trash2 size={11} className="inline mr-1" />Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {filtered.length > 0 && (
          <PaginationControls page={page} pageSize={pageSize} total={total}
            onPage={setPage} onPageSize={setPageSize}
            pageSizeOptions={[25, 50, 100, 200]} />
        )}
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MaterialTransferModal — Create new MTV (Material or Asset)
// ═══════════════════════════════════════════════════════════════════════
function MaterialTransferModal({ onClose, onSaved }) {
  const [projects, setProjects] = useState([]);
  const [batches, setBatches] = useState([]);
  const [assets, setAssets] = useState([]);
  const [transferType, setTransferType] = useState("Material");   // "Material" | "Asset"
  const [srcId, setSrcId] = useState("");
  const [dstId, setDstId] = useState("");
  const [voucherDate, setVoucherDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [lines, setLines] = useState([{ source_batch_id: "", quantity: "", unit_rate: 0, item_id: "" }]);
  const [assetLines, setAssetLines] = useState([{ asset_id: "" }]);
  const [dstWarehouses, setDstWarehouses] = useState([]);
  const [dwhId, setDwhId] = useState("");
  const [allWarehouses, setAllWarehouses] = useState([]);
  const [srcWhId, setSrcWhId] = useState("");     // WarehouseToProject source
  const [dstWhId2, setDstWhId2] = useState("");   // ProjectToWarehouse destination
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isWhToProj = transferType === "WarehouseToProject";
  const isProjToWh = transferType === "ProjectToWarehouse";
  const usesMaterialLines = transferType !== "Asset";

  useEffect(() => {
    api.get("/projects-picker").then((r) => setProjects(r.data || [])).catch(() => {});
    api.get("/warehouses", { params: { limit: 500 } })
      .then((r) => setAllWarehouses((r.data || []).filter((w) => w.is_active !== false)))
      .catch(() => {});
  }, []);

  // When the source (project or warehouse) or type changes, load the correct
  // source inventory via the eligible-stock helper (rules match backend).
  useEffect(() => {
    const params = { transfer_type: transferType };
    if (isWhToProj) {
      if (!srcWhId) { setBatches([]); return; }
      params.source_warehouse_id = srcWhId;
    } else {
      if (!srcId) { setBatches([]); setAssets([]); return; }
      params.source_project_id = srcId;
    }
    api.get(`/material-transfers-bulk/eligible-stock`, { params })
      .then((r) => {
        if (transferType === "Asset") setAssets(r.data || []);
        else setBatches(r.data || []);
      })
      .catch(() => { setBatches([]); setAssets([]); });
  }, [srcId, srcWhId, transferType]);   // eslint-disable-line

  // Destination warehouse — hybrid: auto-pick the destination branch's
  // default warehouse, user may override with any warehouse of that branch.
  useEffect(() => {
    if (!dstId || isWhToProj || isProjToWh) { setDstWarehouses([]); setDwhId(""); return; }
    const bid = projects.find((x) => x.id === dstId)?.branch_id;
    if (!bid) { setDstWarehouses([]); setDwhId(""); return; }
    api.get("/warehouses", { params: { branch_id: bid, limit: 500 } })
      .then((r) => {
        const rows = (r.data || []).filter((w) => w.is_active !== false);
        setDstWarehouses(rows);
        const def = rows.find((w) => w.is_default) || rows[0];
        setDwhId(def?.id || "");
      })
      .catch(() => { setDstWarehouses([]); setDwhId(""); });
  }, [dstId, projects]);

  // Reset lines when switching type so stale data doesn't leak across
  useEffect(() => {
    if (transferType === "Asset") {
      setAssetLines([{ asset_id: "" }]);
    } else {
      setLines([{ source_batch_id: "", quantity: "", unit_rate: 0, item_id: "" }]);
    }
    setSrcWhId(""); setDstWhId2("");
  }, [transferType]);

  const onBatchChange = (idx, batchId) => {
    const b = batches.find((x) => x.id === batchId);
    setLines((arr) => arr.map((l, i) => i === idx ? {
      ...l, source_batch_id: batchId,
      item_id: b?.item_id || "", item_code: b?.item_code || "",
      item_name: b?.item_name || "", unit: b?.unit || "",
      unit_rate: b?.unit_rate || 0,
    } : l));
  };
  const onAssetChange = (idx, assetId) => {
    const a = assets.find((x) => x.id === assetId);
    setAssetLines((arr) => arr.map((l, i) => i === idx ? {
      ...l, asset_id: assetId,
      asset_code: a?.asset_code || "", asset_name: a?.name || "",
      category: a?.category || "", serial_number: a?.serial_number || "",
      purchase_value: a?.purchase_value || 0,
    } : l));
  };

  const totalValue = usesMaterialLines
    ? lines.reduce((sum, l) => sum + (Number(l.quantity) || 0) * (Number(l.unit_rate) || 0), 0)
    : assetLines.reduce((sum, l) => sum + (Number(l.purchase_value) || 0), 0);

  // ── Excel bulk template + upload (project→project types only) ─────────
  const canBulk = !isWhToProj && !isProjToWh && srcId && dstId && srcId !== dstId;
  const downloadTemplate = async () => {
    if (!canBulk) { toast.error("Select From and To projects (they must differ)"); return; }
    try {
      const r = await api.get(`/material-transfers-bulk/template`, {
        params: { transfer_type: transferType, source_project_id: srcId, destination_project_id: dstId },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      const src = projects.find((p) => p.id === srcId);
      link.download = `MTV_${transferType}_Template_${src?.project_code || "PROJ"}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`${transferType} template downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download template");
    }
  };
  const uploadTemplate = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";                   // allow re-uploading same file
    if (!file) return;
    if (!canBulk) { toast.error("Select From and To projects first"); return; }
    if (!reason.trim()) { toast.error("Transfer reason / remarks is required before uploading"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("transfer_type", transferType);
      form.append("source_project_id", srcId);
      form.append("destination_project_id", dstId);
      form.append("transfer_reason", reason);
      form.append("voucher_date", voucherDate);
      form.append("destination_warehouse_id", dwhId);
      const r = await api.post(`/material-transfers-bulk/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data.ok === false && Array.isArray(r.data.errors)) {
        toast.error(`${r.data.errors.length} row(s) failed validation — see console`);
        console.table(r.data.errors);
        return;
      }
      const v = r.data.voucher;
      toast.success(`${v.voucher_number} · ${v.status === "Approved" ? "auto-approved" : "submitted for approval"} · ${r.data.row_count} row(s)`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (isWhToProj) {
      if (!srcWhId || !dstId) { toast.error("Source warehouse and destination Ops project are required"); return; }
    } else if (isProjToWh) {
      if (!srcId || !dstWhId2) { toast.error("Source project and destination warehouse are required"); return; }
    } else if (!srcId || !dstId || srcId === dstId) {
      toast.error("Source and Destination projects must be set and must differ");
      return;
    }
    if (!reason.trim()) { toast.error("Transfer reason is required"); return; }
    setSaving(true);
    try {
      let payload;
      if (usesMaterialLines) {
        const validLines = lines.filter((l) => l.source_batch_id && Number(l.quantity) > 0);
        if (validLines.length === 0) { toast.error("Add at least one valid material line"); setSaving(false); return; }
        payload = {
          transfer_type: transferType,
          voucher_date: voucherDate, transfer_reason: reason,
          line_items: validLines.map((l) => ({
            item_id: l.item_id, item_code: l.item_code, item_name: l.item_name,
            source_batch_id: l.source_batch_id,
            quantity: Number(l.quantity), unit_rate: Number(l.unit_rate),
          })),
        };
        if (isWhToProj) {
          payload.source_warehouse_id = srcWhId;
          payload.destination_project_id = dstId;
        } else if (isProjToWh) {
          payload.source_project_id = srcId;
          payload.destination_warehouse_id = dstWhId2;
        } else {
          payload.source_project_id = srcId;
          payload.destination_project_id = dstId;
          payload.destination_warehouse_id = dwhId;
        }
      } else {
        const validAssets = assetLines.filter((l) => l.asset_id);
        if (validAssets.length === 0) { toast.error("Select at least one asset"); setSaving(false); return; }
        payload = {
          transfer_type: "Asset",
          source_project_id: srcId, destination_project_id: dstId,
          destination_warehouse_id: dwhId,
          voucher_date: voucherDate, transfer_reason: reason,
          asset_line_items: validAssets.map((l) => ({
            asset_id: l.asset_id,
            asset_code: l.asset_code, asset_name: l.asset_name,
            category: l.category, serial_number: l.serial_number,
            purchase_value: Number(l.purchase_value || 0),
          })),
        };
      }
      const r = await api.post("/material-transfers", payload);
      toast.success(`MTV ${r.data.voucher_number} ${r.data.status === "Approved" ? "approved" : "submitted for approval"}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create MTV");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="mtv-modal">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Inventory · New Transfer"
        title="Create Material / Asset Transfer"
        subtitle="Move stock or asset ownership between projects — approval-gated"
        actions={<button onClick={onClose} className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900" data-testid="mtv-modal-close">
          <X size={16} className="inline mr-1" />Close
        </button>}
      />

      {/* Transfer Type toggle */}
      <Card>
        <div className="p-5 flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Transfer Type *</span>
          <div className="inline-flex border border-slate-300 overflow-hidden">
            {Object.keys(TYPE_LABELS).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTransferType(t)}
                className={`px-4 py-1.5 text-xs uppercase tracking-wider font-bold ${
                  transferType === t
                    ? "bg-[#F97316] text-white"
                    : "bg-white text-slate-700 hover:bg-slate-50"
                }`}
                data-testid={`mtv-type-toggle-${t.toLowerCase()}`}
              >
                {TYPE_LABELS[t]}
              </button>
            ))}
          </div>
          <span className="text-[11px] text-slate-500 ml-2">
            {transferType === "Material"
              ? "Inventory batches — quantity-based, posts Transfer_Out/In events"
              : transferType === "Asset"
              ? "Assets — ownership flip, no P&L impact, writes AssetMovement audit"
              : isWhToProj
              ? "Internal warehouse stock → the Ops project's Stock section (consumable via Material Consumption)"
              : "Unused Ops-project stock returns to an internal warehouse as standard inventory"}
          </span>
        </div>
      </Card>

      {/* Header — From / To / Date / Reason */}
      <Card>
        <div className="grid md:grid-cols-2 gap-4 p-5">
          <div>
            {isWhToProj ? (<>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">From Warehouse (Source) *</label>
              <SearchableSelect
                value={srcWhId}
                onChange={(id) => setSrcWhId(id)}
                items={allWarehouses}
                getLabel={(w) => `${w.warehouse_code ? `${w.warehouse_code} — ` : ""}${w.warehouse_name}${w.branch_name ? ` · ${w.branch_name}` : ""}`}
                placeholder="Select internal warehouse…"
                searchPlaceholder="Search warehouse…"
                className="mt-1"
                inputClassName="px-3 py-2 text-sm"
                testid="mtv-source-warehouse"
              />
            </>) : (<>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">From Project (Source) *</label>
              <SearchableSelect
                value={srcId}
                onChange={(id) => setSrcId(id)}
                items={isProjToWh ? projects.filter((p) => p.project_ops_enabled) : projects}
                getLabel={(p) => `${p.project_code} — ${p.project_name}`}
                placeholder={isProjToWh ? "Select Ops project…" : "Select source…"}
                searchPlaceholder="Search project code / name…"
                className="mt-1"
                inputClassName="px-3 py-2 text-sm"
                testid="mtv-source-project"
              />
            </>)}
          </div>
          <div>
            {isProjToWh ? (<>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">To Warehouse (Destination) *</label>
              <SearchableSelect
                value={dstWhId2}
                onChange={(id) => setDstWhId2(id)}
                items={allWarehouses}
                getLabel={(w) => `${w.warehouse_code ? `${w.warehouse_code} — ` : ""}${w.warehouse_name}${w.branch_name ? ` · ${w.branch_name}` : ""}`}
                placeholder="Select internal warehouse…"
                searchPlaceholder="Search warehouse…"
                className="mt-1"
                inputClassName="px-3 py-2 text-sm"
                testid="mtv-dest-warehouse-return"
              />
            </>) : (<>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">{isWhToProj ? "To Ops Project (Stock Section) *" : "To Project (Destination) *"}</label>
              <SearchableSelect
                value={dstId}
                onChange={(id) => setDstId(id)}
                items={isWhToProj ? projects.filter((p) => p.project_ops_enabled) : projects.filter((p) => p.id !== srcId)}
                getLabel={(p) => `${p.project_code} — ${p.project_name}`}
                placeholder={isWhToProj ? "Select Ops project…" : "Select destination…"}
                searchPlaceholder="Search project code / name…"
                className="mt-1"
                inputClassName="px-3 py-2 text-sm"
                testid="mtv-dest-project"
              />
            </>)}
          </div>
          {!isWhToProj && !isProjToWh && (
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Destination Warehouse *</label>
            <SearchableSelect
              value={dwhId}
              onChange={(id) => setDwhId(id)}
              items={dstWarehouses}
              getLabel={(w) => `${w.warehouse_name}${w.is_default ? " (default)" : ""}`}
              disabled={!dstId}
              placeholder={!dstId ? "Pick destination project first"
                : dstWarehouses.length ? "Select warehouse…" : "No active warehouse in destination branch"}
              searchPlaceholder="Search warehouse…"
              className="mt-1"
              inputClassName="px-3 py-2 text-sm"
              testid="mtv-dest-warehouse"
            />
            <div className="text-[10px] text-slate-400 mt-1">
              Auto-picked from the destination branch&apos;s default warehouse — override if the material/asset lands elsewhere.
            </div>
          </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Transfer Date *</label>
            <input type="date" value={voucherDate} onChange={(e) => setVoucherDate(e.target.value)}
              className="w-full mt-1 border border-slate-300 px-3 py-2 text-sm focus:border-[#F97316] focus:outline-none"
              data-testid="mtv-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Transfer Reason / Remarks *</label>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
              className="w-full mt-1 border border-slate-300 px-3 py-2 text-sm focus:border-[#F97316] focus:outline-none"
              placeholder="e.g. Reposition surplus from Phase I to Phase III"
              data-testid="mtv-reason" />
          </div>
        </div>
      </Card>

      {/* Bulk Excel actions — project→project types only */}
      {!isWhToProj && !isProjToWh && (
      <Card>
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600 flex items-center gap-2">
              <FileSpreadsheet size={13} className="text-[#F97316]" />
              Bulk Upload (optional)
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Download the pre-filled template of the source project&apos;s
              {transferType === "Material" ? " stock, mark transfer quantities" : " Available assets, keep only what you want to move"} and re-upload.
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <button
              type="button"
              onClick={downloadTemplate}
              disabled={!canBulk}
              className="text-xs uppercase tracking-wider font-bold text-slate-700 hover:text-[#F97316] px-3 py-1.5 border border-slate-300 disabled:opacity-40 flex items-center gap-1"
              data-testid="mtv-download-template-btn"
            >
              <Download size={12} /> Download Template
            </button>
            <label
              className={`text-xs uppercase tracking-wider font-bold px-3 py-1.5 border border-slate-300 flex items-center gap-1 cursor-pointer ${
                canBulk && !uploading ? "text-slate-700 hover:text-[#F97316]" : "opacity-40 pointer-events-none"
              }`}
              data-testid="mtv-upload-template-label"
            >
              <Upload size={12} /> {uploading ? "Uploading…" : "Upload Filled Template"}
              <input type="file" accept=".xlsx" onChange={uploadTemplate}
                className="hidden" data-testid="mtv-upload-template-input" />
            </label>
          </div>
        </div>
      </Card>
      )}

      {/* Line items table — Material or Asset */}
      {usesMaterialLines ? (
      <Card>
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">Material Lines · Manual Entry</div>
          <button
            type="button"
            onClick={() => setLines([...lines, { source_batch_id: "", quantity: "", unit_rate: 0, item_id: "" }])}
            className="text-xs uppercase tracking-wider font-bold text-[#F97316] hover:underline"
            data-testid="mtv-add-line"
          >
            <Plus size={11} className="inline mr-1" />Add Line
          </button>
        </div>
        <table className="w-full text-sm" data-testid="mtv-lines-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-600 font-bold">
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Batch (Item · Remaining)</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Unit Rate</th>
              <th className="px-3 py-2 text-right">Value (₹)</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, idx) => {
              const lineValue = (Number(l.quantity) || 0) * (Number(l.unit_rate) || 0);
              return (
                <tr key={idx} className="border-b border-slate-100">
                  <td className="px-3 py-2 font-bold text-slate-600">{idx + 1}</td>
                  <td className="px-3 py-2">
                    <select
                      value={l.source_batch_id || ""}
                      onChange={(e) => onBatchChange(idx, e.target.value)}
                      className="w-full border border-slate-300 px-2 py-1 text-sm focus:border-[#F97316] focus:outline-none"
                      data-testid={`mtv-line-batch-${idx}`}
                    >
                      <option value="">{(isWhToProj ? srcWhId : srcId) ? "Choose batch…" : (isWhToProj ? "Select source warehouse first" : "Select source project first")}</option>
                      {batches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {`${b.batch_code} · ${b.item_code || b.item_name} · ${b.remaining_qty} ${b.unit || "Nos"} @ ₹${b.unit_rate}`}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input type="number" min="0" step="0.01"
                      value={l.quantity}
                      onChange={(e) => setLines((arr) => arr.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x))}
                      className="w-24 border border-slate-300 px-2 py-1 text-right text-sm focus:border-[#F97316] focus:outline-none tabular-nums"
                      data-testid={`mtv-line-qty-${idx}`} />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                    {l.unit_rate ? `₹${Number(l.unit_rate).toLocaleString("en-IN")}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold text-slate-900">
                    {lineValue > 0 ? formatINR(lineValue) : "—"}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {lines.length > 1 && (
                      <button onClick={() => setLines((arr) => arr.filter((_, i) => i !== idx))}
                              className="text-rose-600 hover:text-rose-800" data-testid={`mtv-line-remove-${idx}`}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className="bg-slate-50 font-bold border-t-2 border-slate-300">
              <td colSpan={4} className="px-3 py-3 text-right text-[10px] uppercase tracking-wider">TOTAL VALUE</td>
              <td className="px-3 py-3 text-right tabular-nums text-slate-900" data-testid="mtv-total-value">
                {formatINR(totalValue)}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </Card>
      ) : (
      <Card>
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">Asset Lines · Manual Entry</div>
          <button
            type="button"
            onClick={() => setAssetLines([...assetLines, { asset_id: "" }])}
            className="text-xs uppercase tracking-wider font-bold text-[#F97316] hover:underline"
            data-testid="mtv-add-asset-line"
          >
            <Plus size={11} className="inline mr-1" />Add Asset
          </button>
        </div>
        <table className="w-full text-sm" data-testid="mtv-asset-lines-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-600 font-bold">
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Asset (Code · Name)</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Serial #</th>
              <th className="px-3 py-2 text-right">Purchase Value</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {assetLines.map((l, idx) => (
              <tr key={idx} className="border-b border-slate-100">
                <td className="px-3 py-2 font-bold text-slate-600">{idx + 1}</td>
                <td className="px-3 py-2">
                  <select
                    value={l.asset_id || ""}
                    onChange={(e) => onAssetChange(idx, e.target.value)}
                    className="w-full border border-slate-300 px-2 py-1 text-sm focus:border-[#F97316] focus:outline-none"
                    data-testid={`mtv-asset-line-${idx}`}
                  >
                    <option value="">{srcId ? "Choose asset…" : "Select source project first"}</option>
                    {assets
                      .filter((a) => !assetLines.some((al, ai) => ai !== idx && al.asset_id === a.id))
                      .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.asset_code} · {a.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2 text-slate-600 text-xs">{l.category || "—"}</td>
                <td className="px-3 py-2 text-slate-600 text-xs font-mono">{l.serial_number || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {l.purchase_value ? `₹${Number(l.purchase_value).toLocaleString("en-IN")}` : "—"}
                </td>
                <td className="px-3 py-2 text-center">
                  {assetLines.length > 1 && (
                    <button onClick={() => setAssetLines((arr) => arr.filter((_, i) => i !== idx))}
                            className="text-rose-600 hover:text-rose-800" data-testid={`mtv-asset-line-remove-${idx}`}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            <tr className="bg-slate-50 font-bold border-t-2 border-slate-300">
              <td colSpan={4} className="px-3 py-3 text-right text-[10px] uppercase tracking-wider">TOTAL PURCHASE VALUE</td>
              <td className="px-3 py-3 text-right tabular-nums text-slate-900" data-testid="mtv-asset-total-value">
                {formatINR(totalValue)}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </Card>
      )}

      {/* Sticky save bar */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-5 py-3 flex items-center justify-between gap-3 sticky bottom-4">
        <div className="text-xs text-slate-600">
          {transferType === "Material"
            ? "Material transfers post Transfer_Out/In cost events (no P&L impact). Batches update on approval."
            : "Asset transfers flip project ownership on approval. Asset status stays Available."}
        </div>
        <div className="flex gap-3">
          <button onClick={onClose}
            className="text-xs uppercase tracking-wider font-bold text-slate-600 hover:text-slate-900 px-4 py-2 border border-slate-300"
            data-testid="mtv-cancel-btn"
          >Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="bg-[#F97316] text-white text-xs uppercase tracking-wider font-bold px-5 py-2 disabled:opacity-40"
            data-testid="mtv-save-btn"
          >
            {saving ? "Saving…" : "Save & Submit for Approval"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MaterialTransferView — read-only detail tab
// ═══════════════════════════════════════════════════════════════════════
function MaterialTransferView({ mtv: initial, onClose, onReload }) {
  const [m, setM] = useState(initial);
  useEffect(() => {
    api.get(`/material-transfers/${initial.id}`).then((r) => setM(r.data)).catch(() => {});
  }, [initial.id]);
  const isAsset = (m.transfer_type || "Material") === "Asset";
  return (
    <div className="space-y-6" data-testid="mtv-view-tab">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow={`Inventory · MTV · ${m.transfer_type || "Material"} · ${m.status}`}
        title={m.voucher_number}
        subtitle={`${m.source_project_name} → ${m.destination_project_name}  ·  ${(m.voucher_date || "").slice(0, 10)}`}
        actions={<button onClick={onClose} className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900">
          <X size={16} className="inline mr-1" />Close
        </button>}
      />
      <Card>
        <div className="grid md:grid-cols-2 gap-px bg-slate-100">
          <Field label="Transfer Type" value={TYPE_LABELS[m.transfer_type] || m.transfer_type || "Material"} />
          <Field label="Source" value={m.source_project_name} />
          <Field label="Destination Project" value={m.destination_project_name} />
          <Field label="Transfer Date" value={(m.voucher_date || "").slice(0, 10)} />
          <Field label="FY" value={m.fy_code} />
          <Field label="Branch" value={m.branch_name} />
          <Field label="Total Value" value={formatINR(m.total_value)} />
          <Field label="Reason / Remarks" value={m.transfer_reason} wide />
          <Field label="Status" value={m.status} />
          <Field label="Approved By" value={formatUser(m.approved_by, "—")} />
          <Field label="Approved At" value={(m.approved_at || "").slice(0, 19).replace("T", " ") || "—"} />
          {m.uploaded_via === "excel_bulk" && (
            <Field label="Uploaded Via" value={`Excel · ${m.uploaded_filename || ""}`} wide />
          )}
        </div>
      </Card>
      {isAsset ? (
      <Card>
        <table className="w-full text-sm" data-testid="mtv-view-asset-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] uppercase tracking-wider font-bold text-slate-600">
              <th className="px-4 py-2">#</th>
              <th className="px-4 py-2">Asset Code</th>
              <th className="px-4 py-2">Asset Name</th>
              <th className="px-4 py-2">Category</th>
              <th className="px-4 py-2">Serial #</th>
              <th className="px-4 py-2 text-right">Purchase Value (₹)</th>
              <th className="px-4 py-2">Remarks</th>
            </tr>
          </thead>
          <tbody>
            {(m.asset_line_items || []).map((l, idx) => (
              <tr key={idx} className="border-b border-slate-100">
                <td className="px-4 py-2.5 font-bold">{idx + 1}</td>
                <td className="px-4 py-2.5 text-[11px] font-mono">{l.asset_code}</td>
                <td className="px-4 py-2.5">{l.asset_name}</td>
                <td className="px-4 py-2.5 text-[11px] text-slate-600">{l.category || "—"}</td>
                <td className="px-4 py-2.5 text-[11px] font-mono text-slate-600">{l.serial_number || "—"}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-bold">{formatINR(l.purchase_value)}</td>
                <td className="px-4 py-2.5 text-[11px] text-slate-600">{l.remarks || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      ) : (
      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] uppercase tracking-wider font-bold text-slate-600">
              <th className="px-4 py-2">#</th>
              <th className="px-4 py-2">Item</th>
              <th className="px-4 py-2">Source Batch</th>
              <th className="px-4 py-2">Destination Batch</th>
              <th className="px-4 py-2">Origin PI</th>
              <th className="px-4 py-2 text-right">Qty</th>
              <th className="px-4 py-2 text-right">Rate</th>
              <th className="px-4 py-2 text-right">Value (₹)</th>
            </tr>
          </thead>
          <tbody>
            {(m.line_items || []).map((l, idx) => (
              <tr key={idx} className="border-b border-slate-100">
                <td className="px-4 py-2.5 font-bold">{idx + 1}</td>
                <td className="px-4 py-2.5">
                  <div className="font-medium text-slate-900">{l.item_name}</div>
                  <div className="text-[10px] font-mono text-slate-500">{l.item_code}</div>
                </td>
                <td className="px-4 py-2.5 text-[11px] font-mono">{l.source_batch_code}</td>
                <td className="px-4 py-2.5 text-[11px] font-mono">{l.destination_batch_code || "—"}</td>
                <td className="px-4 py-2.5 text-[11px] font-mono">{l.source_pi_number}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{l.quantity} {l.unit}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">₹{Number(l.unit_rate).toLocaleString("en-IN")}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-bold">{formatINR(l.line_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      )}
    </div>
  );
}

function Field({ label, value, wide }) {
  return (
    <div className={`bg-white px-4 py-3 ${wide ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5">{value || "—"}</div>
    </div>
  );
}
